import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { format, startOfMonth, subDays } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  Target, Calendar as CalendarIcon, TrendingUp,
  DollarSign, Scissors, Package, Star, Sparkles, Trophy,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtMoney = (n) => '$' + Number(n || 0).toLocaleString('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const pctOf = (current, goal) => {
  const g = Number(goal || 0);
  if (g <= 0) return null;
  return Math.round((Number(current || 0) / g) * 100);
};
const barColor = (pct) => {
  if (pct == null) return 'bg-zinc-700';
  if (pct >= 100) return 'bg-green-500';
  if (pct >= 70) return 'bg-gold';
  if (pct >= 40) return 'bg-yellow-500';
  return 'bg-red-500';
};

function MiniBar({ current, goal, label, icon: Icon, formatter, testId }) {
  const pct = pctOf(current, goal);
  const width = pct == null ? 0 : Math.min(pct, 100);
  const hasGoal = goal != null && Number(goal) > 0;
  return (
    <div className="space-y-1" data-testid={testId}>
      <div className="flex items-center justify-between text-xs">
        <div className="flex items-center gap-1.5 text-zinc-400">
          <Icon className="w-3 h-3" />
          <span>{label}</span>
        </div>
        <div className="text-right">
          <span className="text-white font-medium">{formatter(current)}</span>
          {hasGoal && <span className="text-zinc-500"> / {formatter(goal)}</span>}
          {pct != null && (
            <span className={`ml-2 text-[10px] font-semibold ${pct >= 100 ? 'text-green-400' : pct >= 70 ? 'text-gold' : 'text-zinc-500'}`}>
              {pct}%
            </span>
          )}
        </div>
      </div>
      <div className="h-1 w-full bg-zinc-800 rounded-full overflow-hidden">
        <div className={`h-full ${barColor(pct)} transition-all`} style={{ width: `${width}%` }} />
      </div>
    </div>
  );
}

export default function BarberGoalsRankingSection({ getAuthHeaders }) {
  const [dateRange, setDateRange] = useState({
    from: startOfMonth(new Date()),
    to: new Date(),
  });
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('all');
  const [sortBy, setSortBy] = useState('overall'); // overall|ticket|services|products|extra|neurone
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/branches`, getAuthHeaders())
      .then(r => setBranches(r.data || []))
      .catch(() => setBranches([]));
  }, [getAuthHeaders]);

  const fetchData = useCallback(async () => {
    if (!dateRange?.from || !dateRange?.to) return;
    setLoading(true);
    try {
      const start = format(dateRange.from, 'yyyy-MM-dd');
      const end = format(dateRange.to, 'yyyy-MM-dd');
      const branchParam = selectedBranch !== 'all' ? `&branch_id=${selectedBranch}` : '';
      const res = await axios.get(
        `${API}/barbers/sales-summary?start_date=${start}&end_date=${end}${branchParam}`,
        getAuthHeaders()
      );
      setData(res.data);
    } catch (e) {
      toast.error('Error al cargar avance de metas');
    } finally {
      setLoading(false);
    }
  }, [dateRange, selectedBranch, getAuthHeaders]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const rows = useMemo(() => {
    if (!data?.barbers) return [];
    const enriched = data.barbers
      .filter(b => !b.exclude_from_ranking && b.role_type !== 'receptionist')
      .map(b => {
        const g = b.goals || {};
        const pcts = {
          ticket: pctOf(b.avg_ticket, g.avg_ticket),
          services: pctOf(b.services_count, g.services),
          products: pctOf(b.products_revenue, g.products_revenue),
          extra: pctOf(b.extra_services_count, g.extra_services),
          neurone: pctOf(b.neurone_revenue, g.neurone_revenue),
        };
        const values = Object.values(pcts).filter(v => v != null);
        const overall = values.length > 0 ? Math.round(values.reduce((a, b) => a + b, 0) / values.length) : 0;
        return { ...b, pcts, overall };
      });
    const sorted = [...enriched];
    switch (sortBy) {
      case 'ticket': sorted.sort((a, b) => (b.pcts.ticket ?? -1) - (a.pcts.ticket ?? -1)); break;
      case 'services': sorted.sort((a, b) => (b.pcts.services ?? -1) - (a.pcts.services ?? -1)); break;
      case 'products': sorted.sort((a, b) => (b.pcts.products ?? -1) - (a.pcts.products ?? -1)); break;
      case 'extra': sorted.sort((a, b) => (b.pcts.extra ?? -1) - (a.pcts.extra ?? -1)); break;
      case 'neurone': sorted.sort((a, b) => (b.pcts.neurone ?? -1) - (a.pcts.neurone ?? -1)); break;
      default: sorted.sort((a, b) => b.overall - a.overall);
    }
    return sorted;
  }, [data, sortBy]);

  const formatRangeLabel = () => {
    if (!dateRange?.from) return 'Seleccionar fechas';
    if (!dateRange?.to) return format(dateRange.from, 'dd MMM yyyy', { locale: es });
    return `${format(dateRange.from, 'dd MMM', { locale: es })} - ${format(dateRange.to, 'dd MMM yyyy', { locale: es })}`;
  };

  const setQuickRange = (kind) => {
    const today = new Date();
    if (kind === 'month') setDateRange({ from: startOfMonth(today), to: today });
    else if (kind === '7') setDateRange({ from: subDays(today, 7), to: today });
    else if (kind === '30') setDateRange({ from: subDays(today, 30), to: today });
    else if (kind === '90') setDateRange({ from: subDays(today, 90), to: today });
    setCalendarOpen(false);
  };

  const handleDateSelect = (range) => {
    if (range?.from) {
      setDateRange({ from: range.from, to: range.to || range.from });
      if (range.to) setCalendarOpen(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="barber-goals-ranking-section">
      {/* Filters row */}
      <Card className="bg-zinc-900/50 border-zinc-800 p-4">
        <div className="flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-3">
            {/* Date range */}
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="justify-start text-left font-normal bg-zinc-900 border-zinc-800 text-white hover:bg-zinc-800 hover:text-white min-w-[240px]"
                  data-testid="goals-date-range-picker"
                >
                  <CalendarIcon className="mr-2 h-4 w-4 text-gold" />
                  {formatRangeLabel()}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-zinc-900 border-zinc-800" align="start">
                <div className="flex gap-2 p-3 border-b border-zinc-800">
                  <Button variant="ghost" size="sm" onClick={() => setQuickRange('month')} className="text-xs text-zinc-400 hover:text-white hover:bg-zinc-800">Mes en curso</Button>
                  <Button variant="ghost" size="sm" onClick={() => setQuickRange('7')} className="text-xs text-zinc-400 hover:text-white hover:bg-zinc-800">7 días</Button>
                  <Button variant="ghost" size="sm" onClick={() => setQuickRange('30')} className="text-xs text-zinc-400 hover:text-white hover:bg-zinc-800">30 días</Button>
                  <Button variant="ghost" size="sm" onClick={() => setQuickRange('90')} className="text-xs text-zinc-400 hover:text-white hover:bg-zinc-800">90 días</Button>
                </div>
                <Calendar
                  initialFocus
                  mode="range"
                  defaultMonth={dateRange.from}
                  selected={dateRange}
                  onSelect={handleDateSelect}
                  numberOfMonths={2}
                  locale={es}
                  className="text-white"
                  classNames={{
                    months: 'flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0',
                    month: 'space-y-4',
                    caption: 'flex justify-center pt-1 relative items-center text-white',
                    caption_label: 'text-sm font-medium text-white',
                    nav_button: 'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 text-white border border-zinc-700 rounded-sm hover:bg-zinc-800',
                    nav_button_previous: 'absolute left-1',
                    nav_button_next: 'absolute right-1',
                    head_cell: 'text-zinc-500 rounded-md w-8 font-normal text-[0.8rem]',
                    cell: 'relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-gold/20',
                    day: 'h-8 w-8 p-0 font-normal aria-selected:opacity-100 text-white hover:bg-zinc-800 rounded-sm',
                    day_range_start: 'day-range-start bg-gold text-black hover:bg-gold hover:text-black',
                    day_range_end: 'day-range-end bg-gold text-black hover:bg-gold hover:text-black',
                    day_selected: 'bg-gold text-black hover:bg-gold hover:text-black focus:bg-gold focus:text-black',
                    day_today: 'bg-zinc-800 text-gold',
                    day_outside: 'day-outside text-zinc-600',
                    day_disabled: 'text-zinc-700 opacity-50',
                    day_range_middle: 'aria-selected:bg-gold/20 aria-selected:text-white',
                    day_hidden: 'invisible',
                  }}
                />
              </PopoverContent>
            </Popover>

            {/* Branch */}
            <Select value={selectedBranch} onValueChange={setSelectedBranch}>
              <SelectTrigger className="w-[200px] bg-zinc-900 border-zinc-800 text-white" data-testid="goals-branch-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                <SelectItem value="all" className="text-white">Todas las sucursales</SelectItem>
                {branches.map(b => (
                  <SelectItem key={b.id} value={b.id} className="text-white">{b.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Sort by */}
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[220px] bg-zinc-900 border-zinc-800 text-white" data-testid="goals-sort-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                <SelectItem value="overall" className="text-white">Ordenar: Promedio general</SelectItem>
                <SelectItem value="ticket" className="text-white">Ordenar: Ticket promedio</SelectItem>
                <SelectItem value="services" className="text-white">Ordenar: Servicios</SelectItem>
                <SelectItem value="products" className="text-white">Ordenar: Productos</SelectItem>
                <SelectItem value="extra" className="text-white">Ordenar: EXTRA</SelectItem>
                <SelectItem value="neurone" className="text-white">Ordenar: Neurone</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="text-xs text-zinc-500 flex items-center gap-2">
            <Target className="w-3.5 h-3.5" />
            <span>Metas mensuales · el % es tu avance dentro del rango seleccionado</span>
          </div>
        </div>
      </Card>

      {loading ? (
        <div className="text-center py-12 text-zinc-500" data-testid="goals-loading">Cargando avance...</div>
      ) : rows.length === 0 ? (
        <Card className="bg-zinc-900/50 border-zinc-800 border-dashed p-12 text-center" data-testid="goals-empty">
          <Target className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
          <p className="text-zinc-400">Sin datos para el rango seleccionado</p>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="goals-ranking-list">
          {rows.map((b, idx) => (
            <Card key={b.barber_id} className="bg-zinc-900/50 border-zinc-800 p-4" data-testid={`goals-row-${b.barber_id}`}>
              <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                {/* Rank + Name */}
                <div className="flex items-center gap-3 lg:w-64 shrink-0">
                  <div className={`w-9 h-9 rounded-sm flex items-center justify-center text-sm font-heading ${
                    idx === 0 ? 'bg-gold text-black' : idx === 1 ? 'bg-zinc-400 text-black' : idx === 2 ? 'bg-amber-700 text-white' : 'bg-zinc-800 text-zinc-400'
                  }`}>
                    {idx < 3 ? <Trophy className="w-4 h-4" /> : `#${idx + 1}`}
                  </div>
                  <div className="min-w-0">
                    <p className="text-white font-medium truncate">{b.name}</p>
                    <p className="text-xs text-zinc-500 truncate">{b.branch_name || '—'}</p>
                  </div>
                </div>

                {/* Overall pct */}
                <div className="lg:w-32 shrink-0">
                  <p className="text-xs text-zinc-500 uppercase tracking-widest">Promedio</p>
                  <p className={`font-heading text-2xl ${b.overall >= 100 ? 'text-green-500' : b.overall >= 70 ? 'text-gold' : b.overall >= 40 ? 'text-yellow-500' : 'text-red-500'}`}>
                    {b.overall}%
                  </p>
                </div>

                {/* 5 mini bars */}
                <div className="flex-1 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
                  <MiniBar
                    label="Ticket"
                    icon={DollarSign}
                    current={b.avg_ticket}
                    goal={b.goals?.avg_ticket}
                    formatter={fmtMoney}
                    testId={`bar-ticket-${b.barber_id}`}
                  />
                  <MiniBar
                    label="Servicios"
                    icon={Scissors}
                    current={b.services_count}
                    goal={b.goals?.services}
                    formatter={(v) => v}
                    testId={`bar-services-${b.barber_id}`}
                  />
                  <MiniBar
                    label="Productos"
                    icon={Package}
                    current={b.products_revenue}
                    goal={b.goals?.products_revenue}
                    formatter={fmtMoney}
                    testId={`bar-products-${b.barber_id}`}
                  />
                  <MiniBar
                    label="EXTRA"
                    icon={Star}
                    current={b.extra_services_count}
                    goal={b.goals?.extra_services}
                    formatter={(v) => v}
                    testId={`bar-extra-${b.barber_id}`}
                  />
                  <MiniBar
                    label="Neurone"
                    icon={Sparkles}
                    current={b.neurone_revenue}
                    goal={b.goals?.neurone_revenue}
                    formatter={fmtMoney}
                    testId={`bar-neurone-${b.barber_id}`}
                  />
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
